import React, { useState } from "react";
import PropTypes from "prop-types";
import "./UserSystemCard.css";

// Step components
import UserStep1Intro from "./userSteps/UserStep1Intro";
import UserStep2AddUsers from "./userSteps/UserStep2AddUsers";
import UserStep3ApprovalGate from "./userSteps/UserStep3ApprovalGate";
import UserStep4ActionsApprovalGate from "./userSteps/UserStep4ActionsApprovalGate ";

const UserSystemCard = ({ companyStructure, userSystem, onChange }) => {
  // 1) Which step are we on? (1-4)
  const [cardStep, setCardStep] = useState(1);

  // 2) For the completion animation
  const [isCompleted, setIsCompleted] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  // --- Step 2: Group-Level (handleCheckboxChange) ---
  const handleCheckboxChange = (targetGroup, sourceGroup) => {
    const oldArray = userSystem.addUserConfig[targetGroup] || [];
    let newArray;
    if (oldArray.includes(sourceGroup)) {
      newArray = oldArray.filter((g) => g !== sourceGroup);
    } else {
      newArray = [...oldArray, sourceGroup];
    }

    onChange({
      ...userSystem,
      addUserConfig: {
        ...userSystem.addUserConfig,
        [targetGroup]: newArray,
      },
    });
  };

  // --- Step 2: Role-Level (handleRoleCheckboxChange) ---
  const getRoleKey = (groupName, roleName) => `${groupName}||${roleName}`;

  const handleRoleCheckboxChange = (targetKey, sourceKey) => {
    const oldArray = userSystem.addRoleConfig?.[targetKey] || [];
    let newArray;
    if (oldArray.includes(sourceKey)) {
      newArray = oldArray.filter((r) => r !== sourceKey);
    } else {
      newArray = [...oldArray, sourceKey];
    }

    onChange({
      ...userSystem,
      addRoleConfig: {
        ...(userSystem.addRoleConfig || {}),
        [targetKey]: newArray,
      },
    });
  };

  // --- Step 3: ApprovalGate handlers ---
  const handleApprovalRequiredCheckbox = (e) => {
    onChange({
      ...userSystem,
      approvalGate: {
        ...userSystem.approvalGate,
        isRequired: e.target.checked,
      },
    });
  };

  const handleGroupApprovalCheckbox = (groupName) => {
    const existing = userSystem.approvalGate?.groupApprovals?.[groupName];
    if (!existing) return;

    const updated = {
      ...existing,
      approvalRequired: !existing.approvalRequired,
    };

    onChange({
      ...userSystem,
      approvalGate: {
        ...userSystem.approvalGate,
        groupApprovals: {
          ...userSystem.approvalGate.groupApprovals,
          [groupName]: updated,
        },
      },
    });
  };

  const handleGroupApproverRoleChange = (groupName, newRole) => {
    const existing = userSystem.approvalGate?.groupApprovals?.[groupName];
    if (!existing) return;

    const updated = {
      ...existing,
      approverRole: newRole,
    };

    onChange({
      ...userSystem,
      approvalGate: {
        ...userSystem.approvalGate,
        groupApprovals: {
          ...userSystem.approvalGate.groupApprovals,
          [groupName]: updated,
        },
      },
    });
  };

  /**
   * Called at the end of Step 4 (the final step).
   * Shows a big "User System Complete" checkmark,
   * then slides out after 2 seconds and removes from DOM.
   */
  const finishFlow = () => {
    // Switch to the "complete" screen
    setIsCompleted(true);

    // After 2s, remove from DOM (and do a slide-out animation)
    setTimeout(() => {
      setIsHidden(true);
    }, 2000);
  };

  // If the card is hidden, render nothing
  if (isHidden) {
    return null;
  }

  // If we've completed all steps, show a big green check screen
  if (isCompleted) {
    return (
      <div className="user-system-card slide-out-left">
        <div className="completion-container">
          <div className="green-check">✔</div>
          <h2 className="completion-title">User System Complete</h2>
        </div>
      </div>
    );
  }

  // Otherwise, we show the normal wizard flow (Steps 1 -> 4)
  return (
    <div className="user-system-card system-card">
      {cardStep === 1 && <UserStep1Intro setCardStep={setCardStep} />}

      {cardStep === 2 && (
        <UserStep2AddUsers
          setCardStep={setCardStep}
          companyStructure={companyStructure}
          userSystem={userSystem}
          onChange={onChange}
          handleCheckboxChange={handleCheckboxChange}
          handleRoleCheckboxChange={handleRoleCheckboxChange}
          getRoleKey={getRoleKey}
          // any other props your step 2 needs, e.g. handleCheckboxChange
        />
      )}

      {cardStep === 3 && (
        <UserStep3ApprovalGate
          setCardStep={setCardStep}
          companyStructure={companyStructure}
          userSystem={userSystem}
          onChange={onChange}
          // We no longer call finishFlow here, we go to step 4
        />
      )}

      {cardStep === 4 && (
        <UserStep4ActionsApprovalGate
          setCardStep={setCardStep}
          companyStructure={companyStructure}
          userSystem={userSystem}
          onChange={onChange}
          finishFlow={finishFlow} // final
        />
      )}
    </div>
  );
};

UserSystemCard.propTypes = {
  companyStructure: PropTypes.object,
  userSystem: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default UserSystemCard;
